import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Shield, ShieldAlert, ShieldCheck, ExternalLink } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';

const RISK_COLORS = { high: '#ef4444', medium: '#f59e0b', low: '#22c55e' };

export default function AuditRiskPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');

  const { data, isLoading } = useQuery({
    queryKey: ['audit-risk'],
    queryFn: async () => {
      const r = await apiClient.get('/compliance/audit-risk');
      return r.data.data;
    },
  });

  const clients = data?.clients || [];

  // Only show Xero-connected clients
  const xeroClients = clients.filter((c: any) => c.xeroConnected);

  const filtered = xeroClients.filter((c: any) => {
    if (filterLevel !== 'all' && c.riskLevel !== filterLevel) return false;
    if (search) {
      const s = search.toLowerCase();
      return c.clientName?.toLowerCase().includes(s) || c.industry?.toLowerCase().includes(s);
    }
    return true;
  });

  const xeroHigh = xeroClients.filter((c: any) => c.riskLevel === 'high').length;
  const xeroMedium = xeroClients.filter((c: any) => c.riskLevel === 'medium').length;
  const xeroLow = xeroClients.filter((c: any) => c.riskLevel === 'low').length;

  const pieData = [
    { name: 'High Risk', value: xeroHigh, fill: RISK_COLORS.high },
    { name: 'Medium Risk', value: xeroMedium, fill: RISK_COLORS.medium },
    { name: 'Low Risk', value: xeroLow, fill: RISK_COLORS.low },
  ].filter(d => d.value > 0);

  // Top 10 highest risk for bar chart (Xero connected only)
  const topRisk = xeroClients.slice(0, 10).map((c: any) => ({
    name: c.clientName.length > 20 ? c.clientName.substring(0, 18) + '...' : c.clientName,
    score: c.riskScore,
    fill: RISK_COLORS[c.riskLevel as keyof typeof RISK_COLORS],
  }));

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={ShieldAlert}
        title="ATO Audit Risk Predictor"
        description="Identify clients at risk of ATO audit based on financial ratios vs industry benchmarks"
      />


      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="pt-3 pb-3">
            <p className="text-[10px] text-muted-foreground uppercase">Total Clients</p>
            <p className="text-lg font-bold">{xeroClients.length}</p>
            <p className="text-[9px] text-muted-foreground">{clients.length - xeroClients.length} without accounting</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 dark:border-red-900">
          <CardContent className="pt-3 pb-3">
            <p className="text-[10px] text-red-500 uppercase">High Risk</p>
            <p className="text-lg font-bold text-red-500">{xeroHigh}</p>
          </CardContent>
        </Card>
        <Card className="border-yellow-200 dark:border-yellow-900">
          <CardContent className="pt-3 pb-3">
            <p className="text-[10px] text-yellow-600 uppercase">Medium Risk</p>
            <p className="text-lg font-bold text-yellow-600">{xeroMedium}</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 dark:border-green-900">
          <CardContent className="pt-3 pb-3">
            <p className="text-[10px] text-green-600 uppercase">Low Risk</p>
            <p className="text-lg font-bold text-green-600">{xeroLow}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-3 pb-3">
            <p className="text-[10px] text-muted-foreground uppercase">Avg Score</p>
            <p className={cn('text-lg font-bold', (data?.avgRisk || 0) >= 60 ? 'text-red-500' : (data?.avgRisk || 0) >= 30 ? 'text-yellow-600' : 'text-green-600')}>
              {data?.avgRisk || 0}/100
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {pieData.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Risk Distribution</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="35%" cy="50%" outerRadius={70} innerRadius={35} activeShape={null as any}>
                    {pieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <Tooltip cursor={false} contentStyle={{ backgroundColor: 'rgba(30,41,59,0.95)', border: 'none', borderRadius: 8, color: '#e2e8f0', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }} itemStyle={{ color: '#94a3b8' }} labelStyle={{ color: '#e2e8f0' }} wrapperStyle={{ outline: 'none' }} />
                  <Legend layout="vertical" align="right" verticalAlign="middle" iconType="square" wrapperStyle={{ fontSize: '11px' }} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
        {topRisk.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Highest Risk Clients</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={topRisk} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={100} />
                  <Tooltip cursor={false} contentStyle={{ backgroundColor: 'rgba(30,41,59,0.95)', border: 'none', borderRadius: 8, color: '#e2e8f0', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }} itemStyle={{ color: '#94a3b8' }} labelStyle={{ color: '#e2e8f0' }} wrapperStyle={{ outline: 'none' }} />
                  <Bar dataKey="score" name="Risk Score" radius={[0, 4, 4, 0]} activeBar={false}>
                    {topRisk.map((d: any, i: number) => <Cell key={i} fill={d.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Client Risk Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm">All Clients - Audit Risk Assessment</CardTitle>
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                {['all', 'high', 'medium', 'low'].map(f => (
                  <button key={f} onClick={() => setFilterLevel(f)}
                    className={cn('px-2 py-0.5 text-[10px] rounded-md font-medium capitalize', filterLevel === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80')}>
                    {f}
                  </button>
                ))}
              </div>
              <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="w-48 h-7 text-xs" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-3 py-2 text-left font-medium w-8">Risk</th>
                  <th className="px-3 py-2 text-left font-medium">Client</th>
                  <th className="px-3 py-2 text-left font-medium">Industry</th>
                  <th className="px-3 py-2 text-left font-medium w-16">Score</th>
                  <th className="px-3 py-2 text-left font-medium">Accounting</th>
                  <th className="px-3 py-2 text-left font-medium">Risk Factors</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((client: any) => (
                  <tr key={client.clientId} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/panel/compliance/audit-risk/${client.clientId}`, { state: { client } })}>
                    <td className="px-3 py-2">
                      {client.riskLevel === 'high' ? <ShieldAlert className="h-4 w-4 text-red-500" /> :
                       client.riskLevel === 'medium' ? <Shield className="h-4 w-4 text-yellow-500" /> :
                       <ShieldCheck className="h-4 w-4 text-green-500" />}
                    </td>
                    <td className="px-3 py-2 font-medium">{client.clientName}</td>
                    <td className="px-3 py-2 text-muted-foreground">{client.industry || <span className="text-red-400 italic">Not set</span>}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden w-12">
                          <div className={cn('h-full rounded-full', client.riskLevel === 'high' ? 'bg-red-500' : client.riskLevel === 'medium' ? 'bg-yellow-500' : 'bg-green-500')}
                            style={{ width: `${client.riskScore}%` }} />
                        </div>
                        <span className={cn('text-[10px] font-bold', client.riskLevel === 'high' ? 'text-red-500' : client.riskLevel === 'medium' ? 'text-yellow-600' : 'text-green-600')}>
                          {client.riskScore}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {client.xeroConnected ? <Badge variant="outline" className="text-[10px] text-green-600">Connected</Badge> : <Badge variant="outline" className="text-[10px] text-muted-foreground">No</Badge>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="space-y-0.5 max-w-md">
                        {client.risks.slice(0, 3).map((risk: string, i: number) => (
                          <div key={i} className="flex items-start gap-1">
                            <AlertTriangle className="h-3 w-3 text-yellow-500 mt-0.5 flex-shrink-0" />
                            <span className="text-[10px] text-muted-foreground">{risk}</span>
                          </div>
                        ))}
                        {client.risks.length > 3 && <span className="text-[10px] text-muted-foreground">+{client.risks.length - 3} more</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
